import Honeybadger from '@honeybadger-io/js'
import {
  type ErrorReportingConfig,
  isBrowserNoise,
  parseErrorReportingConfig,
  scrubBreadcrumb,
  scrubNotice,
} from '@/lib/error-scrubbing'
import { preloadErrorRecovery } from '@/lib/preload-error'

let reporting: ErrorReportingConfig | null = null

export function initErrorReporting(): void {
  const config = parseErrorReportingConfig(
    document.querySelector<HTMLMetaElement>('meta[name="honeybadger"]')?.content,
  )
  if (!config) return

  Honeybadger.configure({
    apiKey: config.apiKey,
    environment: config.environment,
    revision: config.revision,
    breadcrumbsEnabled: { dom: false, network: true, navigation: true, console: false },
    insights: { enabled: false, console: false, http: false },
  })

  Honeybadger.setContext({
    ...(config.userId === undefined ? {} : { user_id: config.userId }),
    ...(config.tenantId === undefined ? {} : { tenant_id: config.tenantId }),
  })

  Honeybadger.beforeBreadcrumb((breadcrumb) => scrubBreadcrumb(breadcrumb, config.locators))

  Honeybadger.beforeNotify((notice) => {
    if (!notice) return
    if (preloadErrorRecovery.reloading(notice.originalError)) return false
    if (isBrowserNoise(notice.message, document.URL)) return false
    scrubNotice(notice, config.locators, document.referrer)
  })

  reporting = config
}

export const reportingClient: {
  event(type: string, payload: Record<string, string | number | boolean | null>): void
  flushAsync(): Promise<void>
} = {
  event: (type, payload) => Honeybadger.event(type, payload),
  flushAsync: () => Honeybadger.flushAsync(),
}

export function setReportingPerson({
  userId,
  tenantId,
}: {
  userId: number | null
  tenantId: number | null
}): void {
  if (!reporting) return
  Honeybadger.setContext({ user_id: userId ?? undefined, tenant_id: tenantId ?? undefined })
}

export function beforeEvent(handler: Parameters<typeof Honeybadger.beforeEvent>[0]): void {
  Honeybadger.beforeEvent(handler)
}

export function errorReportingConfigured(): boolean {
  return reporting !== null
}

export function reportingRevision(): string | null {
  return reporting?.revision ?? null
}

export function reportError(error: Error, context: Record<string, string>): void {
  if (reporting) Honeybadger.notify(error, { context })
}
